import MasterCrudPage from "../../components/MasterCrudPage";
import {
  addressMasterApi,
  orderMasterApi,
  shipmentTrackingApi,
} from "../../api/masterApi";

function toDatetimeLocal(value) {
  if (!value) return "";
  return String(value).replace("T", " ").slice(0, 16).replace(" ", "T");
}

function addressLabel(address) {
  const parts = [
    address.address_line_1,
    address.city_name,
    address.postal_code,
  ].filter(Boolean);
  return parts.join(", ") || "Address";
}

function orderLabel(order) {
  const parts = [order.service_title, order.order_status_name].filter(Boolean);
  if (order.order_datetime) {
    parts.push(String(order.order_datetime).replace("T", " ").slice(0, 16));
  }
  return parts.join(" · ") || "Order";
}

export default function ShipmentTracking() {
  return (
    <MasterCrudPage
      title="Shipment Tracking"
      description="Track shipment partners and delivery progress for orders."
      idKey="tracking_id"
      api={shipmentTrackingApi}
      listLabel="shipment tracking records"
      useJson
      loadExtraOptions={async () => {
        const [orders, addresses] = await Promise.all([
          orderMasterApi.list(),
          addressMasterApi.list(),
        ]);

        const orderOptions = (orders || []).map((order) => ({
          value: order.order_id,
          label: orderLabel(order),
          order_datetime: toDatetimeLocal(order.order_datetime),
          dispatch_address_id: order.dispatch_address_id,
          seller_user_id: order.seller_user_id,
        }));

        const orderLocations = [];
        for (const order of orders || []) {
          const sellerAddresses = (addresses || []).filter(
            (address) => String(address.user_id) === String(order.seller_user_id)
          );
          for (const address of sellerAddresses) {
            orderLocations.push({
              value: address.address_id,
              label: addressLabel(address),
              parentId: order.order_id,
            });
          }
        }

        return {
          orders: orderOptions,
          orderLocations,
        };
      }}
      fields={[
        {
          name: "order_id",
          label: "Order",
          type: "select",
          required: true,
          optionsKey: "orders",
          placeholder: "Select order",
          inheritFields: [
            { from: "order_datetime", to: "order_datetime" },
            { from: "dispatch_address_id", to: "order_location_id" },
          ],
        },
        {
          name: "order_datetime",
          label: "Order Date & Time",
          type: "datetime-local",
        },
        {
          name: "order_location_id",
          label: "Order Location (Seller Address)",
          type: "select",
          optionsKey: "orderLocations",
          filterBy: "order_id",
          placeholder: "Select seller address",
        },
        {
          name: "shipment_partner_id",
          label: "Shipment Partner",
          type: "text",
          placeholder: "Enter partner",
        },
        {
          name: "shipment_partner_name",
          label: "Shipment Partner Name",
          type: "text",
          placeholder: "Enter partner name",
        },
        {
          name: "partner_tracking_id",
          label: "Partner Tracking",
          type: "text",
          placeholder: "Enter partner tracking",
        },
        {
          name: "partner_tracking_status",
          label: "Partner Tracking Status",
          type: "text",
          placeholder: "Enter partner tracking status",
        },
        {
          name: "partner_tracking_datetime",
          label: "Partner Tracking Date & Time",
          type: "datetime-local",
        },
        {
          name: "dispatch_datetime",
          label: "Dispatch Date & Time",
          type: "datetime-local",
        },
        {
          name: "delivery_datetime",
          label: "Shipping/ Delivery Date & Time",
          type: "datetime-local",
        },
      ]}
      columns={[
        { key: "tracking_id", label: "Tracking" },
        { key: "order_id", label: "Order" },
        {
          key: "order_datetime",
          label: "Order Date & Time",
          render: (item) =>
            item.order_datetime
              ? String(item.order_datetime).replace("T", " ").slice(0, 16)
              : "—",
        },
        {
          key: "order_location_id",
          label: "Order Location",
          render: (item) =>
            item.order_location_line || "—",
        },
        { key: "shipment_partner_id", label: "Partner" },
        { key: "shipment_partner_name", label: "Partner Name" },
        { key: "partner_tracking_id", label: "Partner Tracking" },
        { key: "partner_tracking_status", label: "Partner Status" },
        {
          key: "partner_tracking_datetime",
          label: "Partner Date & Time",
          render: (item) =>
            item.partner_tracking_datetime
              ? String(item.partner_tracking_datetime).replace("T", " ").slice(0, 16)
              : "—",
        },
        {
          key: "dispatch_datetime",
          label: "Dispatch Date & Time",
          render: (item) =>
            item.dispatch_datetime
              ? String(item.dispatch_datetime).replace("T", " ").slice(0, 16)
              : "—",
        },
        {
          key: "delivery_datetime",
          label: "Shipping/ Delivery Date & Time",
          render: (item) =>
            item.delivery_datetime
              ? String(item.delivery_datetime).replace("T", " ").slice(0, 16)
              : "—",
        },
      ]}
    />
  );
}
